/**
 * Mock data for the /preview/swipe-picks UX experiment.
 * Standalone — no DB/auth. Shapes mirror GameData / PropData so we can render
 * the REAL UnifiedGameCard (inline boosts included) to test the swipe gesture
 * against live slider controls.
 */
import type { GameData, PropData, MyPropData } from '@/services/events/main-event';
import type { PropResult, PropType, PickMode } from '@prisma/client';

export type MockGroup = { name: string; games: GameData[] };

const baseTime = new Date('2026-06-14T18:00:00Z');

function game(
  group: string,
  i: number,
  away: string,
  home: string,
  awaySeed: number,
  homeSeed: number,
): GameData {
  return {
    id: `${group}-${i}`,
    homeTeam: home,
    awayTeam: away,
    gameTime: baseTime,
    lockTime: baseTime,
    homeScore: null,
    awayScore: null,
    winner: null,
    homeSeed,
    awaySeed,
    group,
  };
}

/** Build SPREAD boost props for one game (both teams, 5 alt-lines each) so the
 *  inline slider renders — this is the slider-vs-swipe collision test. */
function spreadProps(g: GameData): PropData[] {
  const lines = [-2.5, -1.5, -0.5, 0.5, 1.5];
  const mults = [2.6, 1.8, 1.3, 0.95, 0.75];
  const out: PropData[] = [];
  for (const team of [g.awayTeam, g.homeTeam]) {
    lines.forEach((line, idx) => {
      out.push({
        id: `${g.id}-spread-${team}-${line}`,
        description: `${team} ${line > 0 ? '+' : ''}${line}`,
        costCoins: 10,
        bonusMultiplier: mults[idx],
        result: 'PENDING' as PropResult,
        gameId: g.id,
        category: 'SPREAD',
        line,
        propType: 'ODDS_BASED' as PropType,
        pickMode: 'BUY_ONLY' as PickMode,
        subjectTeam: team,
        subjectPlayer: null,
        threshold: null,
        statCategory: null,
      });
    });
  }
  return out;
}

export const MOCK_GROUPS: MockGroup[] = [
  {
    name: 'Group A',
    games: [
      game('Group A', 1, 'Brazil', 'Serbia', 1, 18),
      game('Group A', 2, 'Switzerland', 'Cameroon', 12, 24),
      game('Group A', 3, 'Brazil', 'Switzerland', 1, 12),
      game('Group A', 4, 'Serbia', 'Cameroon', 18, 24),
      game('Group A', 5, 'Cameroon', 'Brazil', 24, 1),
      game('Group A', 6, 'Serbia', 'Switzerland', 18, 12),
    ],
  },
  {
    name: 'Group B',
    games: [
      game('Group B', 1, 'France', 'Denmark', 2, 10),
      game('Group B', 2, 'Tunisia', 'Australia', 30, 38),
      game('Group B', 3, 'France', 'Australia', 2, 38),
      game('Group B', 4, 'Denmark', 'Tunisia', 10, 30),
      game('Group B', 5, 'Australia', 'Denmark', 38, 10),
    ],
  },
  {
    name: 'Group C',
    games: [
      game('Group C', 1, 'Argentina', 'Mexico', 3, 13),
      game('Group C', 2, 'Poland', 'Croatia', 26, 5),
      game('Group C', 3, 'Argentina', 'Croatia', 3, 5),
      game('Group C', 4, 'Mexico', 'Poland', 13, 26),
    ],
  },
];

/** 3 Yes/No manual predictions per game. */
function manualProps(g: GameData): PropData[] {
  const questions = [
    { q: 'Both teams to score?', mult: 1.8 },
    { q: 'Over 2.5 goals?', mult: 2.1 },
    { q: `${g.awayTeam} to score first?`, mult: 1.6 },
  ];
  return questions.map((item, idx) => ({
    id: `${g.id}-yn-${idx}`,
    description: item.q,
    costCoins: 0,
    bonusMultiplier: item.mult,
    result: 'PENDING' as PropResult,
    gameId: g.id,
    category: null,
    line: null,
    propType: 'MANUAL_GENERIC' as PropType,
    pickMode: 'YES_NO' as PickMode,
    subjectTeam: null,
    subjectPlayer: null,
    threshold: null,
    statCategory: null,
  }));
}

export const MOCK_ALL_GAMES: GameData[] = MOCK_GROUPS.flatMap((grp) => grp.games);

/** Every game gets a spread slider + 3 Yes/No predictions (prototype scope). */
export const MOCK_PROPS: PropData[] = MOCK_ALL_GAMES.flatMap((g) => [
  ...spreadProps(g),
  ...manualProps(g),
]);

const propIndex = new Map<string, PropData>(MOCK_PROPS.map((p) => [p.id, p]));

/** Build a faithful MyPropData entry from a purchase so the real card can show
 *  selected/purchased state (YES/NO highlight, spread "Purchased" badge). */
export function buildMyProp(propId: string, side?: 'YES' | 'NO'): MyPropData | null {
  const p = propIndex.get(propId);
  if (!p) return null;
  return {
    undercardPropId: p.id,
    description: p.description,
    costCoins: p.costCoins,
    bonusMultiplier: p.bonusMultiplier,
    bonusPoints: 0,
    result: 'PENDING' as PropResult,
    pickMode: p.pickMode,
    userSelection: side ?? null,
  };
}
